import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import type {
  TaskTrackerRecurringTask,
  TaskTrackerRecurringTaskSavePayload,
} from '../lib/taskTrackerRecurringTasks.types'
import {
  mapRecurringTaskFromApi,
  mapRecurringTaskSavePayloadToApi,
  mapRecurringTasksFromApi,
} from './taskTracker.recurringTasks.api.mappers'
import type {
  ApiTaskTrackerRecurringTaskDeleteResponse,
  ApiTaskTrackerRecurringTaskMutationResponse,
  ApiTaskTrackerRecurringTasksIndexResponse,
} from './taskTracker.recurringTasks.api.types'
import { getApiErrorMessage, TASK_TRACKER_API_REQUEST_CONFIG } from './taskTracker.api.utils'

export const taskTrackerRecurringTasksApi = {
  async listRecurringTasks(
    boardId: number,
  ): Promise<TaskTrackerRecurringTask[]> {
    const res = await API.get<ResponseWrapper<ApiTaskTrackerRecurringTasksIndexResponse>>(
      `task-tracker-recurring-tasks/index${getParams({ board_id: boardId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось загрузить регулярные задачи'),
      )
    }

    return mapRecurringTasksFromApi(res.data?.data?.recurring_tasks)
  },

  async getRecurringTask(
    boardId: number,
    recurringTaskId: number,
  ): Promise<TaskTrackerRecurringTask> {
    const res = await API.get<ResponseWrapper<ApiTaskTrackerRecurringTaskMutationResponse>>(
      `task-tracker-recurring-tasks/view${getParams({
        board_id: boardId,
        recurring_task_id: recurringTaskId,
      })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось загрузить регулярную задачу'),
      )
    }

    if (!res.data?.data?.recurring_task) {
      throw new Error('Не удалось загрузить регулярную задачу')
    }

    return mapRecurringTaskFromApi(res.data.data.recurring_task)
  },

  async createRecurringTask(
    boardId: number,
    payload: TaskTrackerRecurringTaskSavePayload,
  ): Promise<TaskTrackerRecurringTask> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerRecurringTaskMutationResponse>>(
      `task-tracker-recurring-tasks/create${getParams({ board_id: boardId })}`,
      mapRecurringTaskSavePayloadToApi(payload),
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось создать регулярную задачу'),
      )
    }

    if (!res.data?.data?.recurring_task) {
      throw new Error('Не удалось создать регулярную задачу')
    }

    return mapRecurringTaskFromApi(res.data.data.recurring_task)
  },

  async updateRecurringTask(
    boardId: number,
    recurringTaskId: number,
    payload: TaskTrackerRecurringTaskSavePayload,
  ): Promise<TaskTrackerRecurringTask> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerRecurringTaskMutationResponse>>(
      `task-tracker-recurring-tasks/update${getParams({
        board_id: boardId,
        recurring_task_id: recurringTaskId,
      })}`,
      mapRecurringTaskSavePayloadToApi(payload),
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось сохранить регулярную задачу'),
      )
    }

    if (!res.data?.data?.recurring_task) {
      throw new Error('Не удалось сохранить регулярную задачу')
    }

    return mapRecurringTaskFromApi(res.data.data.recurring_task)
  },

  async deleteRecurringTask(
    boardId: number,
    recurringTaskId: number,
  ): Promise<{ deleted: boolean; recurringTaskId: number }> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerRecurringTaskDeleteResponse>>(
      `task-tracker-recurring-tasks/delete${getParams({
        board_id: boardId,
        recurring_task_id: recurringTaskId,
      })}`,
      {},
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось удалить регулярную задачу'),
      )
    }

    return {
      deleted: Boolean(res.data?.data?.deleted),
      recurringTaskId,
    }
  },
}
